import { authoredTextIn, promoteToLocalized, setAuthoredLocale } from "./authoredText.js";
import type { AuthoredText as AuthoredTextT } from "./authoredText.js";
import type { Locale } from "./locale.js";
import type { Draft } from "./programEdit.js";

// The editor's LANGUAGE switch, on its own.
//
// PE-21 slice B, in Antenore's words: *«un selettore di lingua nell'editor
// programmi, e la UI salvi il contenuto nella cache spostandosi da uno
// all'altro»*. The cache is the draft itself: every visible string is
// first grafted back into the language being left, then the strings for
// the language being entered are read out of storage.
//
// ⭐ Split out of programEditActions.ts (PE-40) because that file crossed the
// 500-line cap, and this is the seam that costs nothing to cut: everything
// else there reshapes the draft's STRUCTURE — days, weeks, slots, rows, cells
// — while this one only moves TEXT between languages. It shares no helper with
// them; it is the only reader of authoredText.js in that file.

// ⛔ Promotion to a per-language object happens HERE and nowhere else.
// Slice A promises that a program nobody translates keeps its naked
// strings for ever, saves included. Asking for a second language is the
// moment that promise has done its job — there is nowhere else to put
// the first one.
//
// ⛔ The read is `authoredTextIn`, NOT `pickAuthoredText`: a language
// with nothing in it must come up EMPTY. Falling back would show the
// coach the Italian under a French flag, they would type over it, and the
// Italian would be gone while the French was never written.
//
// Returns the draft untouched when there is no language to leave (a Plan
// draft, `editLocale: null`) or when the target is the current one.
export const changeEditLocale = (draft: Draft, next: Locale): Draft => {
  const from = draft.editLocale;
  if (!from || from === next) return draft;

  // The string on screen belongs to the language being left. Carry it
  // into storage, then read the target language back out.
  const carry = (
    raw: AuthoredTextT | undefined,
    text: string,
  ): AuthoredTextT | undefined =>
    setAuthoredLocale(promoteToLocalized(raw, from) ?? {}, from, text);

  const swap = (
    raw: AuthoredTextT | undefined,
    text: string,
  ): { raw: AuthoredTextT | undefined; text: string } => {
    const carried = carry(raw, text);
    return { raw: carried, text: authoredTextIn(carried, next) };
  };

  const name = swap(draft.nameRaw, draft.name);
  const description = swap(draft.descriptionRaw, draft.description);

  return {
    ...draft,
    editLocale: next,
    name: name.text,
    nameRaw: name.raw,
    description: description.text,
    descriptionRaw: description.raw,
    sessions: draft.sessions.map((s) => {
      const sName = swap(s.nameRaw, s.name);
      const sNotes = swap(s.notesRaw, s.notes);
      return {
        ...s,
        name: sName.text,
        nameRaw: sName.raw,
        notes: sNotes.text,
        notesRaw: sNotes.raw,
        slots: s.slots.map((sl) => {
          const instr = swap(sl.instructionsRaw, sl.instructions);
          return {
            ...sl,
            instructions: instr.text,
            instructionsRaw: instr.raw,
            setRows: sl.setRows.map((r) => {
              const swapped = r.notesPerCell.map((text, w) =>
                swap(r.notesRawPerCell?.[w], text),
              );
              return {
                ...r,
                notesPerCell: swapped.map((c) => c.text),
                notesRawPerCell: swapped.map((c) => c.raw),
              };
            }),
          };
        }),
      };
    }),
  };
};
